import { FiberManualRecord as FiberManualRecordIcon } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { createProjectVenuePreset } from '@/domain/project-venue-presets/actions/create-project-venue-preset.action';
import { VenuePreset } from '@/domain/project-venue-presets/entities/project-venue-presets.entity.ts';
import { selectProjectVenuesPresetsItems } from '@/domain/project-venue-presets/project-venue-presets.selectors.ts';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton.tsx';
import { PageTitle } from '@/ui/components/PageTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { VenuePresetForm } from '@/ui/pages/app/project/venue/presets/components/VenuePresetForm.tsx';
import { ColorsRaw } from '@/ui/theme/colors-raw.ts';

export function VenuePresets() {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectCurrentProjectVenue);
  const presets = useAppSelector(selectProjectVenuesPresetsItems);

  const navigate = useNavigate();

  const [selectedPreset, setSelectedPreset] =
    React.useState<VenuePreset | null>(null);

  useEffect(() => {
    const selectFirstPresetOnLoad = () => {
      if (selectedPreset === null && presets.length > 0) {
        setSelectedPreset(presets[0]);
      }
    };
    selectFirstPresetOnLoad();
  }, [presets, selectedPreset]);

  if (project === null) {
    return null;
  }

  const onClickBack = () => {
    navigate(`/app/projects/venue/${project.id}/dates`);
  };

  const handleClickCreate = () => {
    if (project && project.organisationId) {
      const newPreset: VenuePreset = {
        presetTitle: translate('ProjectsVenue.Presets.new-preset-name'),
        avgCapacity: 0,
        mobility: [],
        equipment: [],
        accommodation: [],
        projectId: project.id,
        organisationId: project.organisationId,
      };
      dispatch(createProjectVenuePreset({ presetForm: newPreset }));
    }
  };

  const presetElements = [];

  for (const preset of presets) {
    presetElements.push(
      <Button
        key={preset.id}
        onClick={() => setSelectedPreset(preset)}
        size="large"
        className={selectedPreset?.id === preset.id ? 'selected' : ''}
        startIcon={<FiberManualRecordIcon sx={{ color: preset.presetColor }} />}
        sx={{
          justifyContent: 'flex-start',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          color: ColorsRaw.basic.Cream,
          textTransform: 'initial',
          '&:hover, &.selected': {
            backgroundColor: ColorsRaw.anthracite.Warm_Anthracite_2,
          },
        }}
      >
        {preset.presetTitle}
      </Button>,
    );
  }

  return (
    <Stack sx={{ gap: 3 }}>
      <PageTitle
        title={translate('ProjectsVenue.Presets.my-presets')}
        backButtonText={project?.name}
        onClickBack={onClickBack}
      />
      <Stack flexDirection="row">
        <Stack
          sx={{
            flex: '1 1 400px',
            minWidth: '400px',
            gap: '24px',
            marginTop: 3,
          }}
        >
          {presetElements}
          <FSPrimaryButton onClick={handleClickCreate}>
            {translate('ProjectsVenue.Presets.create-preset')}
          </FSPrimaryButton>
        </Stack>
        <Stack sx={{ flex: '1 1 100%' }}>
          {selectedPreset && (
            <VenuePresetForm preset={selectedPreset} project={project} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
